import { FormControl, RadioGroup, FormControlLabel, Radio, FormGroup, Checkbox } from '@mui/material';
import { EventPollQuestion, EventPollAnswer, EventInterface, CheckedAnswer } from '../../../interfaces/interfaces';
import VyButton from '../VyButton';
import { PollStats } from './PollStats';
import { Fragment, useEffect, useState } from 'react';
import { uniqBy } from 'lodash';
import { useTranslation } from 'next-i18next';
import toast from 'react-hot-toast';
import usePoll from '../../../hooks/usePoll';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { MBWPollStats } from '../../event/templates/MBW/MBWPoll/MBWPollStats';
import { useSelector } from '../../../reduxToolkit/typedRedux';

interface SinglePollProps {
  question: EventPollQuestion;
  allowUnregistered?: boolean;
  event: EventInterface;
  isMBW?: boolean;
  backgroundColor?: string;
  textColor?: string;
  fetch?: () => void;
}

export const SinglePoll = ({ question, allowUnregistered, event, isMBW, backgroundColor, textColor, fetch }: SinglePollProps) => {
  const answeredMeetingPolls = useSelector(({ video }) => video.answeredPollQuestions);
  const user = useSelector((state) => state.common.user);

  const [isAnswered, setIsAnswered] = useState(question.isAnswered);
  const [showResults, setShowResults] = useState(false);
  const [timeoutActive, setTimeoutActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortedAnswers, setSortedAnswers] = useState<EventPollAnswer[]>([]);
  const [checkedAnswers, setCheckedAnswers] = useState<CheckedAnswer[]>([]);
  const [isDisabled, setIsDisabled] = useState<boolean | undefined>(false);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);

  const { t } = useTranslation();
  const { answerPoll, getPollTextColor, getPollBackgroundColor } = usePoll();
  function reactivatePoll() {
    if (!allowUnregistered) return;
    setTimeout(() => {
      setTimeoutActive(false);
      toast.success(t('poll:reactivated'));
    }, 4000);
  }

  const submitPoll = async () => {
    if (checkedAnswers.length === 0) return;
    allowUnregistered && setTimeoutActive(true);
    setIsSubmitting(true);
    await answerPoll(
      question._id,
      checkedAnswers,
      () => {
        if (user) return;
        allowUnregistered && setSelectedAnswers([]);
        reactivatePoll();
        fetch?.();
      },
      false,
      allowUnregistered
    );
    // Hacky solution because the poll's answers are not shown immediately, only after refresh but this keeps it simple until the due rework
    if (user) {
      setIsAnswered(true);
    }
    setIsSubmitting(false);
    setCheckedAnswers([]);
  };

  const handleCheckAnswer = (newAnswer: EventPollAnswer, checked: boolean) => {
    if (checked) {
      setCheckedAnswers((prevCheckedAnswers) =>
        uniqBy(
          [
            ...(question.allowMultiSelect ? prevCheckedAnswers : []),
            {
              answerid: newAnswer._id,
              questionid: question._id,
              date: new Date(),
            },
          ].sort((a, b) => b.date.getTime() - a.date.getTime()),
          'answerid'
        )
      );
    } else {
      setCheckedAnswers((prevCheckedAnswers) =>
        [...(question.allowMultiSelect ? prevCheckedAnswers : [])].filter((answer) => answer.answerid !== newAnswer._id)
      );
    }
  };

  useEffect(() => {
    setSelectedAnswers(checkedAnswers.map((answer) => answer.answerid));
  }, [checkedAnswers]);

  useEffect(() => {
    setIsAnswered(question.isAnswered || answeredMeetingPolls.includes(question._id));
    setSortedAnswers(question.answers?.slice()?.sort((a, b) => (a?.sortIndex ?? 0) - (b?.sortIndex ?? 0)));

    if (allowUnregistered && !user) setSelectedAnswers([]);
    else setSelectedAnswers(question.answers?.filter((answer) => answer.isAnswered).map((answer) => answer._id));
  }, [question, allowUnregistered, answeredMeetingPolls]);

  useEffect(() => {
    setIsDisabled(timeoutActive || isSubmitting || isAnswered);
  }, [timeoutActive, isSubmitting, isAnswered]);

  useEffect(() => {
    setShowResults(question.isShared && (!allowUnregistered || !!user) && !!isAnswered);
  }, [question, allowUnregistered, isAnswered]);

  if (isMBW) {
    return (
      <div
        style={{
          background: 'white',
          color: 'black',
        }}
        className="flex flex-col gap-2 justify-center p-2 mb-2 w-full bg-slate-400">
        <FormControl component="fieldset" className="w-full" disabled={isDisabled || isAnswered}>
          <p style={{ wordBreak: 'normal' }}>{question.content}</p>
          <hr style={{ borderStyle: 'solid', borderColor: 'black' }} className="my-2" />
          {showResults && <PollStats question={question} type="question" />}
          {!question.allowMultiSelect && (
            <RadioGroup className="flex flex-col gap-4 w-full">
              {sortedAnswers?.map((answer: EventPollAnswer) => (
                <Fragment key={answer._id}>
                  <div
                    className="flex flex-col w-full h-14 shadow-sm transition-shadow bg-slate-100 hover:shadow-lg"
                    style={{ backgroundColor: selectedAnswers.includes(answer._id) ? `${event?.template.mainColor}60` : '#ECEEEF' }}>
                    <FormControlLabel
                      control={
                        <Radio style={{ color: isDisabled ? '' : event?.template.mainButtonTextColor }} checked={selectedAnswers.includes(answer._id)} hidden />
                      }
                      className="w-full h-full"
                      label={
                        <span className="px-5 h-full" style={{ fontFamily: event.template.customFont ?? 'Gilroy' }}>
                          {answer.content}
                        </span>
                      }
                      value={answer._id}
                      onChange={() => handleCheckAnswer(answer, true)}
                    />
                  </div>
                  {showResults && (
                    <div className="-mt-2 w-full">
                      <MBWPollStats poll={question} pollAnswer={answer} type="answer" isLoadingOptionActive={false} />
                    </div>
                  )}
                </Fragment>
              ))}
            </RadioGroup>
          )}

          {question.allowMultiSelect && (
            <FormGroup className="flex flex-col gap-4 w-full">
              {sortedAnswers?.map((answer: EventPollAnswer) => (
                <Fragment key={answer._id}>
                  <div
                    className="flex flex-col w-full h-14 shadow-sm transition-shadow bg-slate-100 hover:shadow-lg"
                    style={{ backgroundColor: selectedAnswers.includes(answer._id) ? `${event?.template.mainColor}60` : '#ECEEEF' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAnswers.includes(answer._id)}
                          style={{ color: isDisabled ? '' : event?.template.mainButtonTextColor }}
                          hidden
                        />
                      }
                      className="w-full h-full"
                      label={
                        <span className="px-5 h-full" style={{ fontFamily: event.template.customFont ?? 'Gilroy' }}>
                          {answer.content}
                        </span>
                      }
                      value={answer._id}
                      onChange={(_, checked) => {
                        handleCheckAnswer(answer, checked);
                      }}
                    />
                  </div>
                  {showResults && (
                    <div className="-mt-2 w-full">
                      <MBWPollStats poll={question} pollAnswer={answer} type="answer" isLoadingOptionActive={false} />
                    </div>
                  )}
                </Fragment>
              ))}
            </FormGroup>
          )}
        </FormControl>
        <hr style={{ borderStyle: 'solid', borderColor: 'black' }} className="my-2" />
        {!isAnswered && (
          <VyButton
            disabled={isDisabled}
            onClick={submitPoll}
            showLoadingIcon={isSubmitting}
            event={event}
            style={{ border: `1px solid ${!isDisabled ? event?.template.mainButtonTextColor : 'transparent'}` }}
            className="rounded-none"
            compact>
            <span className="flex gap-4 justify-center items-center text-center">
              Antwort abgeben {!isSubmitting && <FontAwesomeIcon icon={faCheck} size="1x" />}
            </span>
          </VyButton>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: getPollBackgroundColor(isDisabled, backgroundColor),
        color: getPollTextColor(isDisabled, textColor),
      }}
      className="flex flex-col gap-2 justify-center p-4 mb-2 w-full rounded-lg bg-slate-400">
      <FormControl component="fieldset" className="w-full" disabled={isDisabled || isAnswered}>
        <p className="text-lg font-bold" style={{ wordBreak: 'normal' }}>
          {question.content}
        </p>
        {showResults && <PollStats question={question} type="question" />}
        {!question.allowMultiSelect && (
          <RadioGroup className="w-full">
            {sortedAnswers?.map((answer: EventPollAnswer) => (
              <div className="flex flex-col w-full" key={answer._id}>
                <FormControlLabel
                  control={<Radio style={{ color: getPollTextColor(isDisabled, textColor) }} checked={selectedAnswers.includes(answer._id)} />}
                  className="w-full h-full"
                  label={
                    <span
                      className="text-sm break-normal"
                      style={{ fontFamily: event.template.customFont ?? 'Gilroy', color: getPollTextColor(isDisabled, textColor) }}>
                      {answer.content}
                    </span>
                  }
                  value={answer._id}
                  onChange={() => handleCheckAnswer(answer, true)}
                />
                {showResults && <PollStats question={question} pollAnswer={answer} type="answer" textColor={getPollTextColor(isDisabled, textColor)} />}
              </div>
            ))}
          </RadioGroup>
        )}

        {question.allowMultiSelect && (
          <FormGroup className="w-full">
            {sortedAnswers?.map((answer: EventPollAnswer) => (
              <div key={answer._id} className="flex flex-col w-full">
                <FormControlLabel
                  control={<Checkbox checked={selectedAnswers.includes(answer._id)} style={{ color: getPollTextColor(isDisabled, textColor) }} />}
                  className="w-full h-full"
                  label={
                    <span
                      className="text-sm break-normal"
                      style={{ fontFamily: event.template.customFont ?? 'Gilroy', color: getPollTextColor(isDisabled, textColor) }}>
                      {answer.content}
                    </span>
                  }
                  value={answer._id}
                  onChange={(_, checked) => {
                    handleCheckAnswer(answer, checked);
                  }}
                />
                {showResults && <PollStats question={question} pollAnswer={answer} type="answer" textColor={getPollTextColor(isDisabled, textColor)} />}
              </div>
            ))}
          </FormGroup>
        )}
      </FormControl>
      {!isAnswered && (
        <VyButton
          disabled={isDisabled}
          onClick={submitPoll}
          showLoadingIcon={isSubmitting}
          event={event}
          style={{
            border: `1px solid ${getPollTextColor(isDisabled, textColor)}`,
            color: getPollTextColor(isDisabled, textColor),
            backgroundColor: getPollBackgroundColor(isDisabled, backgroundColor),
          }}>
          {t('poll:poll-submit')}
        </VyButton>
      )}
    </div>
  );
};
